import config from '../config/environment';
import supabase from '../config/supabase';
import type {
  EarlyCheckinFilters,
  EarlyCheckinRow,
} from '../types/earlyCheckin';
import type { PaginatedResponse, PaginationInfo } from '../types/student';
import { handleServiceError } from '../utils/errorHandler';
import { mapProjectIdentity } from '../utils/project';
import { readIsRecentlyReactivated } from '../utils/studentReactivation';
import {
  readIsPregnant,
  readIsRedFlag,
  readIsSensitive,
  readPregnantUntil,
  readSensitiveReason,
} from '../utils/studentSensitive';
import { mapStudentClassificationFromJson } from '../types/studentClassification';

export type EarlyCheckinPaginatedResponse = PaginatedResponse<EarlyCheckinRow>;

type EarlyCheckinMutationAction = 'start' | 'escalate' | 'close';

class EarlyCheckinServiceClass {
  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: EarlyCheckinFilters = {},
  ): Promise<EarlyCheckinPaginatedResponse> {
    const token = await this.getAccessToken();
    const url = new URL(`${config.supabaseFunctionsUrl}/early-checkins`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));

    if (filters.search) url.searchParams.set('search', filters.search);
    if (filters.projectCodes?.length) {
      url.searchParams.set('projectCodes', filters.projectCodes.join(','));
    }
    if (filters.stages?.length) {
      url.searchParams.set('stages', filters.stages.join(','));
    }
    if (filters.sortOrder) url.searchParams.set('sortOrder', filters.sortOrder);
    if (filters.joinedAtStart) url.searchParams.set('joinedAtStart', filters.joinedAtStart);
    if (filters.joinedAtEnd) url.searchParams.set('joinedAtEnd', filters.joinedAtEnd);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(new Error(err?.error || 'Erro ao listar FUP de 8 e 15 dias'), 'listar FUP');
    }

    const body = await response.json() as EarlyCheckinPaginatedResponse & {
      pagination?: Partial<PaginationInfo>;
    };
    const pagination = body.pagination ?? {};
    const resolvedPageSize = Number(pagination.pageSize ?? pageSize);
    const resolvedTotalRecords = Number(pagination.totalRecords ?? 0);
    const resolvedTotalPages = Number.isFinite(Number(pagination.totalPages))
      ? Number(pagination.totalPages)
      : (resolvedTotalRecords === 0 ? 0 : Math.ceil(resolvedTotalRecords / resolvedPageSize));

    return {
      data: (Array.isArray(body.data) ? body.data : []).map((row) => this.mapRow(row)),
      pagination: {
        page: Number(pagination.page ?? page),
        pageSize: resolvedPageSize,
        totalRecords: resolvedTotalRecords,
        totalPages: resolvedTotalPages,
        hasNextPage: Boolean(pagination.hasNextPage),
        hasPreviousPage: Boolean(pagination.hasPreviousPage),
      },
    };
  }

  async start(studentId: number): Promise<void> {
    await this.mutate('start', { student_id: studentId });
  }

  async escalate(studentId: number): Promise<void> {
    await this.mutate('escalate', { student_id: studentId });
  }

  async close(studentId: number, closedNextStep?: string): Promise<void> {
    await this.mutate('close', {
      student_id: studentId,
      ...(closedNextStep?.trim() ? { closed_next_step: closedNextStep.trim() } : {}),
    });
  }

  private async mutate(
    action: EarlyCheckinMutationAction,
    payload: Record<string, unknown>,
  ): Promise<void> {
    const token = await this.getAccessToken();
    const response = await fetch(`${config.supabaseFunctionsUrl}/early-checkins`, {
      method: 'POST',
      headers: this.buildHeaders(token),
      body: JSON.stringify({ action, ...payload }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error || 'Erro ao atualizar o FUP de 8 e 15 dias'),
        'atualizar FUP',
      );
    }
  }

  private async getAccessToken(): Promise<string | undefined> {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token;
  }

  private buildHeaders(token: string | undefined): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: config.supabaseAnonKey,
    };
  }

  private mapRow(raw: EarlyCheckinRow): EarlyCheckinRow {
    return {
      ...raw,
      classification: mapStudentClassificationFromJson(raw.classification),
      project: mapProjectIdentity(raw.project) ?? mapProjectIdentity(raw),
      is_reactivated: readIsRecentlyReactivated(raw),
      is_sensitive: readIsSensitive(raw),
      sensitive_reason: readSensitiveReason(raw),
      is_pregnant: readIsPregnant(raw),
      pregnant_until: readPregnantUntil(raw),
      is_red_flag: readIsRedFlag(raw),
      day8_closed_at: raw.day8_closed_at ?? null,
      day15_closed_at: raw.day15_closed_at ?? null,
      student_notes: Array.isArray(raw.student_notes) ? raw.student_notes : [],
      notes_summary: raw.notes_summary ?? null,
    };
  }
}

export const EarlyCheckinService = new EarlyCheckinServiceClass();
